import React, { useState, useContext } from "react";
import { useTranslation } from 'react-i18next';
import axios from "../../axiosConfig";
import { OwnerContext } from "../../context/OwnerContext";
import { IoMdMale, IoMdFemale } from "react-icons/io";
import { CustomDatePicker } from '../commons/datepicker';
import "./../../styles/OwnerAddDogModal.css";

const OwnerAddDogModal = ({ isOpen, onClose, onDogAdded }) => {
    const { t } = useTranslation(['dogs', 'common']);
    const { owner } = useContext(OwnerContext);
    const [form, setForm] = useState({
        name: "",
        breed: "",
        dateOfBirth: "",
        approximate: false,
        gender: "",
        ownerId: owner ? owner.id : "",
    });
    const [errors, setErrors] = useState({});

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const handleGenderSelect = (genderValue) => {
        setForm((prev) => ({
            ...prev,
            gender: genderValue,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!form.name.trim()) {
            setErrors({ ...errors, name: t('dogs:validation.nameRequired') });
            return;
        }
        if (!form.breed.trim()) {
            setErrors({ ...errors, breed: t('dogs:validation.breedRequired') });
            return;
        }
        if (!form.dateOfBirth) {
            setErrors({ ...errors, dateOfBirth: t('dogs:validation.dateOfBirthRequired') });
            return;
        }
        if (!form.gender) {
            setErrors({ ...errors, gender: t('dogs:validation.genderRequired') });
            return;
        }

        try {
            const response = await axios.post("/api/v1/dog/add-dog", form);
            if (onDogAdded) {
                onDogAdded(response.data);
            }
            setForm({
                name: "",
                breed: "",
                dateOfBirth: "",
                approximate: false,
                gender: "",
                ownerId: owner ? owner.id : "",
            });
            setErrors({});
            if (response.data.pendingApproval) {
                setErrors({ success: t('dogs:addDogPendingApproval') });
                setTimeout(() => {
                    setErrors({});
                    onClose();
                }, 5000);
            } else {
                onClose();
            }
        } catch (error) {
            console.error("Error adding dog:", error);
            if (error.response && error.response.data) {
                setErrors(error.response.data);
            } else {
                setErrors({ general: t('dogs:addDogError') });
            }
        }
    };

    const handleCancel = () => {
        setForm({
            name: "",
            breed: "",
            dateOfBirth: "",
            approximate: false,
            gender: "",
            ownerId: owner ? owner.id : "",
        });
        setErrors({});
        onClose();
    };

    if (!isOpen) return null;

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            handleCancel();
        }
    };

    return (
        <div className="addDogModal__overlay" onClick={handleOverlayClick}>
            <div className="addDogModal__content">
                <h2 className="addDogModal__title">{t('dogs:addDog')}</h2>
                <form className="addDogModal__form">
                    <div className="addDogModal__formGroup">
                        <label htmlFor="name" className="addDogModal__label">{t('dogs:name')}</label>
                        <input
                            id="name"
                            type="text"
                            name="name"
                            className="addDogModal__input"
                            placeholder={t('dogs:placeholder.dogName')}
                            value={form.name}
                            onChange={handleChange}
                            required
                        />
                        {errors.name && <span className="addDogModal__error">{errors.name}</span>}
                    </div>

                    <div className="addDogModal__formGroup">
                        <label htmlFor="breed" className="addDogModal__label">{t('dogs:breed')}</label>
                        <input
                            id="breed"
                            type="text"
                            name="breed"
                            className="addDogModal__input"
                            placeholder={t('dogs:placeholder.breed')}
                            value={form.breed}
                            onChange={handleChange}
                            required
                        />
                        {errors.breed && <span className="addDogModal__error">{errors.breed}</span>}
                    </div>

                    <div className="addDogModal__formGroup">
                        <label htmlFor="dateOfBirth" className="addDogModal__label">{t('dogs:dateOfBirth')}</label>
                        <CustomDatePicker
                            id="dateOfBirth"
                            name="dateOfBirth"
                            className="addDogModal__input"
                            value={form.dateOfBirth}
                            onChange={handleChange}
                            max={new Date().toISOString().split('T')[0]}
                        />
                        {errors.dateOfBirth && <span className="addDogModal__error">{errors.dateOfBirth}</span>}
                    </div>

                    <div className="addDogModal__checkboxGroup">
                        <label htmlFor="approximate" className="addDogModal__checkboxLabel">
                            {t('dogs:approximateAge')}
                            <input
                                id="approximate"
                                type="checkbox"
                                name="approximate"
                                className="addDogModal__checkbox"
                                checked={form.approximate}
                                onChange={handleChange}
                            />
                        </label>
                    </div>

                    <div className="addDogModal__formGroup">
                        <label className="addDogModal__label">{t('dogs:genderLabel')}</label>
                        <div className="addDogModal__genderSelector">
                            <span
                                className={`addDogModal__genderButton ${form.gender === "MALE" ? "addDogModal__genderButton--selected" : ""}`}
                                onClick={() => handleGenderSelect("MALE")}
                                role="button"
                                tabIndex="0"
                                aria-label={t('dogs:male')}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        handleGenderSelect("MALE");
                                    }
                                }}
                            >
                                <IoMdMale className="addDogModal__genderIcon" size={32} />
                                <span style={{ marginLeft: "8px" }}>{t('dogs:male')}</span>
                            </span>
                            <span
                                className={`addDogModal__genderButton ${form.gender === "FEMALE" ? "addDogModal__genderButton--selected" : ""}`}
                                onClick={() => handleGenderSelect("FEMALE")}
                                role="button"
                                tabIndex="0"
                                aria-label={t('dogs:female')}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        handleGenderSelect("FEMALE");
                                    }
                                }}
                            >
                                <IoMdFemale className="addDogModal__genderIcon" size={32} />
                                <span style={{ marginLeft: "8px" }}>{t('dogs:female')}</span>
                            </span>
                        </div>
                        {errors.gender && <span className="addDogModal__error">{errors.gender}</span>}
                    </div>

                    <input type="hidden" name="ownerId" value={form.ownerId} />

                    {errors.success && <p className="addDogModal__success">{errors.success}</p>}

                    <div className="addDogModal__actions">
                        <button type="submit" className="addDogModal__submitButton" onClick={handleSubmit}>
                            {t('common:add')}
                        </button>
                        <button type="button" className="addDogModal__cancelButton" onClick={handleCancel}>
                            {t('common:cancel')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default OwnerAddDogModal;